/**
 * DOM Utility Functions
 * Helper functions for DOM manipulation and querying
 */

/**
 * Safely query a single element
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (defaults to document)
 * @returns {Element|null} - Found element or null
 */
export function querySelector(selector, context = document) {
  try {
    return context.querySelector(selector);
  } catch (error) {
    console.error(`Error querying selector "${selector}":`, error);
    return null;
  }
}

/**
 * Safely query multiple elements
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (defaults to document)
 * @returns {NodeList|Array} - Found elements or empty array
 */
export function querySelectorAll(selector, context = document) {
  try {
    return context.querySelectorAll(selector);
  } catch (error) {
    console.error(`Error querying selector "${selector}":`, error);
    return [];
  }
}

/**
 * Add event listener with error handling
 * @param {Element} element - Target element
 * @param {string} event - Event name
 * @param {Function} handler - Event handler function
 * @param {Object} options - Event listener options
 */
export function addEventListener(element, event, handler, options = {}) {
  if (!element) {
    console.warn('addEventListener: Element is null or undefined');
    return;
  }

  try {
    element.addEventListener(event, handler, options);
  } catch (error) {
    console.error(`Error adding event listener for "${event}":`, error);
  }
}

/**
 * Remove event listener with error handling
 * @param {Element} element - Target element
 * @param {string} event - Event name
 * @param {Function} handler - Event handler function
 * @param {Object} options - Event listener options
 */
export function removeEventListener(element, event, handler, options = {}) {
  if (!element) {
    console.warn('removeEventListener: Element is null or undefined');
    return;
  }

  try {
    element.removeEventListener(event, handler, options);
  } catch (error) {
    console.error(`Error removing event listener for "${event}":`, error);
  }
}

/**
 * Check if element exists in DOM
 * @param {string|Element} elementOrSelector - Element or selector
 * @returns {boolean} - True if element exists
 */
export function elementExists(elementOrSelector) {
  if (typeof elementOrSelector === 'string') {
    return querySelector(elementOrSelector) !== null;
  }
  return elementOrSelector !== null && elementOrSelector !== undefined;
}
